import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { SingleUnderlyingSelector } from 'components/shared/underlyings/selector/single';
import { UnderlyingsGridLegacy } from 'components/shared/underlyings/underlyingsGridLegacy';
import { useGenerateUnderlyingsGridForExcel } from 'hooks/underlyings/use-generate-underlyings-grid-for-excel';
import { useUnderlyingSearchByFormId } from 'hooks/underlyings/use-underlying-search';
import { useCopyToExcel } from 'hooks/useCopyToExcel';
import {
  DEFAULT_STRIKE_METADATA,
  type SingleUnderlyingSearchEditableFieldData,
  type SingleUnderlyingSearchEditableFieldMetadata,
} from 'models/forms/fields';
import type { Underlying } from 'models/underlying';
import type { UnderlyingStrike } from 'models/underlying/base';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { UnderlyingsFieldProps } from './UnderlyingsField.connect';
import { connectUnderlyings } from './UnderlyingsField.connect';
import { updateCutOff, updateStrike, updateWeight } from './utils';

const Component: FC<UnderlyingsFieldProps> = ({ node: { id }, tabId, defaultUnderlyingCodeType, tabIndex }) => {
  const { formatMessage } = useIntl();

  const [underlying, setUnderlying] = useData<Extract<SingleUnderlyingSearchEditableFieldData, Underlying>>(id);
  const isFieldInvalid = useIsFieldInvalid(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const {
    availableStrikeModes = [],
    isReadOnly,
    hasCutOff,
    strike = DEFAULT_STRIKE_METADATA,
  } = useFieldMetadata<SingleUnderlyingSearchEditableFieldMetadata>(id)!;
  const translationKey = useFieldTranslationKey(id);

  const underlyingSearch = useUnderlyingSearchByFormId(tabId, id);

  const underlyingsForExcel = underlying ? [underlying] : [];
  const { underlyingsGroupedByAssetType, generateTableExport } =
    useGenerateUnderlyingsGridForExcel(underlyingsForExcel);

  const assetTypes = useMemo(() => Array.from(underlyingsGroupedByAssetType.keys()), [underlyingsGroupedByAssetType]);

  const onUnderlyingReplaced = useCallback(
    (idToReplace: string, newUnderlying: Underlying) => {
      if (underlying?.id === idToReplace) {
        setUnderlying(newUnderlying);
      }
    },
    [underlying, setUnderlying],
  );

  const onUnderlyingStrikeChanged = useCallback(
    (underlyingId: string, key: keyof UnderlyingStrike, value: unknown) => {
      if (underlying?.id === underlyingId) {
        setUnderlying(updateStrike(underlying, key, value));
      }
    },
    [underlying, setUnderlying],
  );

  const onUnderlyingWeightChanged = useCallback(
    (underlyingId: string, value: number) => {
      if (underlying?.id === underlyingId) {
        setUnderlying(updateWeight(underlying, value));
      }
    },
    [underlying, setUnderlying],
  );

  const onUnderlyingCutOffChanged = useCallback(
    (underlyingId: string, value: string) => {
      if (underlying?.id === underlyingId) {
        setUnderlying(updateCutOff(underlying, value));
      }
    },
    [underlying, setUnderlying],
  );

  const placeholder = useMemo(() => formatMessage({ id: 'underlying.placeholder' }), [formatMessage]);

  const onCopyAll = useCopyToExcel(generateTableExport);
  return (
    <>
      <BlockOrientationFormGroup inputId={id} label={`form.${translationKey}` as any} className={patchedFieldClassName}>
        {!isReadOnly && (
          <SingleUnderlyingSelector
            id={id}
            {...underlyingSearch}
            underlying={underlying ?? undefined}
            onChange={u => setUnderlying(u as Readonly<Underlying>)}
            placeholder={placeholder}
            hasErrors={isFieldInvalid}
            defaultUnderlyingCodeType={defaultUnderlyingCodeType}
            tabIndex={tabIndex}
          />
        )}
      </BlockOrientationFormGroup>
      <UnderlyingsGridLegacy
        fieldId={id}
        strikeModes={availableStrikeModes}
        hasCutOff={hasCutOff}
        isReadOnly={isReadOnly}
        strikeMetadata={strike}
        formId={tabId}
        isUserInternal
        underlyings={underlyingsForExcel}
        assetTypes={assetTypes}
        underlyingsGroupedByAssetType={underlyingsGroupedByAssetType}
        onUnderlyingReplaced={onUnderlyingReplaced}
        onUnderlyingStrikeChanged={onUnderlyingStrikeChanged}
        onUnderlyingWeightChanged={onUnderlyingWeightChanged}
        onUnderlyingCutOffChanged={onUnderlyingCutOffChanged}
        onCopyAll={onCopyAll}
        defaultUnderlyingCodeType={defaultUnderlyingCodeType}
      />
    </>
  );
};

Component.displayName = 'RawSingleUnderlyingField';

export const RealSingleUnderlyingField = connectUnderlyings(Component);
RealSingleUnderlyingField.displayName = 'RealSingleUnderlyingField';
